/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import HttpService from '@/services/http.service';
import AuthService from '@/services/auth.service';
import LicenseService from '@/services/license.service';
import UserService from '@/services/user.service';
import WhatsappService from '@/services/whatsapp.service';
import Hub360DialogService from '@/services/hub360Dialog.service';
import SessionService from '@/services/session.service';

interface Services {
  httpService: HttpService;
  authService: AuthService;
  licenseService: LicenseService;
  userService: UserService;
  whatsappService: WhatsappService;
  hub360DialogService: Hub360DialogService;
  sessionService: typeof SessionService;
}

const ServicesContext = createContext<Services | null>(null);

interface ServicesProviderProps {
  children: ReactNode;
}

export const ServicesProvider: React.FC<ServicesProviderProps> = ({ children }) => {
  const services = useMemo(() => {
    const httpService = new HttpService();
    const authService = new AuthService(httpService);
    const licenseService = new LicenseService(httpService);
    const userService = new UserService(httpService);
    const whatsappService = new WhatsappService(httpService);
    const hub360DialogService = new Hub360DialogService(httpService);

    return {
      httpService,
      authService,
      licenseService,
      userService,
      whatsappService,
      hub360DialogService,
      sessionService: SessionService,
    };
  }, []);

  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
};

// Custom hooks for each service
export const useServices = (): Services => {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error('useServices must be used within ServicesProvider');
  }
  return context;
};

export const useHttpService = (): HttpService => {
  return useServices().httpService;
};

export const useAuthService = (): AuthService => {
  return useServices().authService;
};

export const useLicenseService = (): LicenseService => {
  return useServices().licenseService;
};

export const useUserService = (): UserService => {
  return useServices().userService;
};

export const useWhatsappService = (): WhatsappService => {
  return useServices().whatsappService;
};

export const useHub360DialogService = (): Hub360DialogService => {
  return useServices().hub360DialogService;
};

export const useSessionService = (): typeof SessionService => {
  return useServices().sessionService;
};
